import React, {FC, useState} from 'react';

import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import {
    useRecurly,
    CardNumberElement,
    CardMonthElement,
    CardYearElement,
    CardCvvElement,
} from '@recurly/react-recurly';
import classNames from 'classnames';
import {connect} from 'react-redux';

import {CHECKOUT_PAYMENT_STEP} from 'constants/analytics/events/checkout';
import {ApplicationStoreType} from 'desktop/store';
import {RecurlyErrorType, RecurlyFieldsType} from 'desktop/types/recurlyTypes';

import {PAYMENTS} from '../../constants/paymentStep';
import {setOptionPaymentMethod} from '../../store/reducers/analytics/analytics';
import {sendPayerInfoToSaga} from '../../store/reducers/paymentStep/payerInfo/payerInfo';
import {getBillingAddressForRecurly} from '../../store/selectors/billingAddress/billingAddress';
import {getStatusOfLoadingSaveOrder} from '../../store/selectors/paymentStep/paymentStep';

import {getUserFrendlyRecurlyError} from '../../utils/paymentRecurlyUtils/paymentRecurlyUtils';
import {PaymentFooter} from '../paymentFooter/paymentFooter';

import s from './paymentRecurlyForm.scss';

export type PropsType = {
    fields: RecurlyFieldsType;
    isLoading: boolean;
    title: string;
    runSavePayment: (data: {title: string; token: string; method: string}) => void;
    sendAnalytics: () => void;
};

/**
 * Gets the error message element
 * @param {string} errorText - recurly error
 * @returns {React.ReactElement | null} - error message element
 */
const getErrorMessage = (errorText: string) => {
    if (errorText === 'Card number is invalid') return null;
    return <p className={s.errorMsg}>{errorText}</p>;
};

/**
 * Provides handler and set of inputs from recurly to get a token
 * @param {PropsType} props - current props
 * @return {Node} component
 */
export const PaymentRecurlyForm: FC<PropsType> = ({
    fields,
    isLoading,
    runSavePayment,
    sendAnalytics,
    title,
}) => {
    const recurly = useRecurly();
    const formRef = React.useRef<HTMLFormElement>(null);

    const [isLoadingRecurly, setLoadingRecurly] = useState(false);
    const [error, setError] = useState<Maybe<RecurlyErrorType>>(null);

    const handleSubmit = () => {
        setLoadingRecurly(true);
        setError(null);
        if (formRef.current === null) return;
        recurly.token(formRef.current, (err, token) => {
            setLoadingRecurly(false);
            if (err) {
                // @ts-expect-error type from recurly library is incorrect(
                setError(err);
                sendDesktopAnalyticsEvent(
                    CHECKOUT_PAYMENT_STEP,
                    {
                        // @ts-expect-error type from recurly library is incorrect(
                        message: `Error – ${getUserFrendlyRecurlyError(err)}`,
                        method: 'Recurly',
                    },
                    false,
                );
            } else {
                runSavePayment({
                    method: PAYMENTS.METHOD_RECURLY,
                    title,
                    token: token.id,
                });
                sendAnalytics();
                sendDesktopAnalyticsEvent(CHECKOUT_PAYMENT_STEP, {
                    message: 'CTA - Recurly',
                    method: 'Payment',
                });
            }
        });
    };

    const isFieldInError = (field: string) => error?.fields.includes(field);

    return (
        <form ref={formRef}>
            {Object.entries(fields).map(([keyProp, value]) => (
                <input data-recurly={keyProp} key={keyProp} type="hidden" value={value} />
            ))}
            <div className={s.wrapper}>
                <CardNumberElement
                    className={classNames({[s.error]: isFieldInError('number')})}
                    style={{placeholder: {content: '* Card number'}}}
                />
                {isFieldInError('number') && <p className={s.errorMsg}>Card number is invalid</p>}
                <div className={classNames(s.paymentDetailsGroup, s.paymentDetails)}>
                    <div className={classNames(s.paymentDetailsGroup, s.dateGroup)}>
                        <CardMonthElement
                            className={classNames(s.field, {[s.error]: isFieldInError('month')})}
                            style={{placeholder: {content: '* MM'}}}
                        />
                        <CardYearElement
                            className={classNames(s.field, {[s.error]: isFieldInError('year')})}
                            style={{placeholder: {content: '* YYYY'}}}
                        />
                    </div>

                    <CardCvvElement
                        className={classNames(s.field, {[s.error]: isFieldInError('cvv')})}
                        style={{placeholder: {content: 'CVV'}}}
                    />
                </div>

                <PaymentFooter isLoading={isLoadingRecurly || isLoading} onSubmit={handleSubmit} />

                {error && getErrorMessage(getUserFrendlyRecurlyError(error))}
            </div>
        </form>
    );
};

/**
 * Map state to props
 * @param {ApplicationStoreType} store - redux store
 * @returns {PropsType} props
 */
const mapStateToProps = (store: ApplicationStoreType) => ({
    fields: getBillingAddressForRecurly(store),
    isLoading: getStatusOfLoadingSaveOrder(store),
});

export const PaymentRecurlyFormConnected = connect(mapStateToProps, {
    runSavePayment: sendPayerInfoToSaga,
    sendAnalytics: setOptionPaymentMethod,
})(PaymentRecurlyForm);
